import { IUserLocationFromCloudFront } from '../../constants/types/configuration/userGlobalConfig.types';
import type { IOperatingLocationOverride } from '../../types/global/operatingLocation.types';
import { IUser } from '../../types/global/ListRender.types';
import { Features, IAdControl, IGlobalConfiguration } from '../../types/global/helper';
import type {
    ICampaignDeadlinesConfig,
    ICampaignPlatformConfig,
    ICreatorPayoutFeeConfig,
    IDeliverableTypeConfig,
    IDurationRangeConfig,
    IInfluencerTierConfig,
    IUgcBudgetConfig,
} from '../../types/global/ugcCampaignConfig.types';
import type { ICampaignHostLtdPromotionAdminResponse } from '../../types/global/campaignHostAdmin.types';
import type { ContentScrubEditorState } from '../../types/global/contentScrub.types';
import type { IBuzzbridgeLoyaltyConfig } from '../../types/global/buzzbridgeLoyalty.types';

export const types = {
    GET_CONFIGURATION: 'GET_CONFIGURATION',
    GET_CONFIGURATION_SUCCESS: 'GET_CONFIGURATION_SUCCESS',
    GET_CONFIGURATION_FAILED: 'GET_CONFIGURATION_FAILED',

    CREATE_CONFIGURATION: 'CREATE_CONFIGURATION',
    CREATE_CONFIGURATION_SUCCESS: 'CREATE_CONFIGURATION_SUCCESS',
    CREATE_CONFIGURATION_FAILED: 'CREATE_CONFIGURATION_FAILED',

    UPDATE_USER_CONFIG: 'UPDATE_USER_CONFIG',
    UPDATE_USER_CONFIG_SUCCESS: 'UPDATE_USER_CONFIG_SUCCESS',
    UPDATE_USER_CONFIG_FAILED: 'UPDATE_USER_CONFIG_FAILED',

    GET_GLOBAL_CONFIGURATION: 'GET_GLOBAL_CONFIGURATION',
    GET_GLOBAL_CONFIGURATION_SUCCESS: 'GET_GLOBAL_CONFIGURATION_SUCCESS',
    GET_GLOBAL_CONFIGURATION_FAIL: 'GET_GLOBAL_CONFIGURATION_FAIL',

    UPDATE_GLOBAL_CONFIGURATION: 'UPDATE_GLOBAL_CONFIGURATION',
    UPDATE_GLOBAL_CONFIGURATION_SUCCESS: 'UPDATE_GLOBAL_CONFIGURATION_SUCCESS',
    UPDATE_GLOBAL_CONFIGURATION_FAIL: 'UPDATE_GLOBAL_CONFIGURATION_FAIL',

    GET_USER_LOCATION: 'GET_USER_LOCATION',
    GET_USER_LOCATION_SUCCESS: 'GET_USER_LOCATION_SUCCESS',
    GET_USER_LOCATION_FAIL: 'GET_USER_LOCATION_FAIL',

    STORE_NOTIF_TOKEN: 'STORE_NOTIF_TOKEN',
    RESET_USER_CONFIG: 'RESET_USER_CONFIG',

    SET_GUIDED_TOUR_INDEX: 'SET_GUIDED_TOUR_INDEX',

    SHOW_PROJECT_GUIDE: 'SHOW_PROJECT_GUIDE',

    SET_USER_LOCATION: 'SET_USER_LOCATION',

    SET_OPERATING_LOCATION_OVERRIDE: 'SET_OPERATING_LOCATION_OVERRIDE',
    CLEAR_OPERATING_LOCATION_OVERRIDE: 'CLEAR_OPERATING_LOCATION_OVERRIDE',
    SET_OPERATING_LOCATION_PERSIST_ACROSS_SESSIONS: 'SET_OPERATING_LOCATION_PERSIST_ACROSS_SESSIONS',
    HYDRATE_OPERATING_LOCATION_FROM_BROWSER: 'HYDRATE_OPERATING_LOCATION_FROM_BROWSER',

    FETCH_CAMPAIGN_HOST_LTD_ADMIN: 'FETCH_CAMPAIGN_HOST_LTD_ADMIN',
    FETCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS: 'FETCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS',
    FETCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL: 'FETCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL',

    PATCH_CAMPAIGN_HOST_LTD_ADMIN: 'PATCH_CAMPAIGN_HOST_LTD_ADMIN',
    PATCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS: 'PATCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS',
    PATCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL: 'PATCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL',
};

export interface IConfigurationInitialState {
    fetchingConfig: boolean;
    configError: any;
    creatingConfig: boolean;
    updatingConfig: boolean;
    /**
     * User specific preferences, IP Address, Notifictaion Settings e.t.c
     */
    userSpecificConfiguration?: IUserConfiguration;
    /** Controls App Settings and Avaulable Features, such as
     * Contest rules
     */
    globalConfiguration?: IGlobalConfiguration;
    fetchingGlobalConfig: boolean;
    influencerTiers: IInfluencerTierConfig[];
    fetchingUserLocation: boolean;
    userLocationErrMsgs: any;
    notification_token?: string;
    guidedTourIndex?: number;
    showProjectGuideTutorial?: boolean;
    userCurrentLocation?: IUserLocationFromCloudFront;
    /** Admin-only market override — sent as X-Operating-* on API requests. */
    operatingLocationOverride?: IOperatingLocationOverride | null;
    /** When true, operatingLocationOverride survives logout and applies for any user on this browser. */
    operatingLocationPersistAcrossSessions?: boolean;
    campaignHostLtdAdmin?: ICampaignHostLtdPromotionAdminResponse;
    fetchingCampaignHostLtdAdmin: boolean;
    updatingCampaignHostLtdAdmin: boolean;
    campaignHostLtdAdminError?: unknown;
}

export interface IUserConfiguration {
    user_location: UserLocation;
    notifications: Notifications;
    web_notifications_settings: WebNotificationsSettings;
    web_communication_preference: WebCommunicationPreference;
    language_pref: string;
    phone_number_code: string;
    timezone: string;
    devices: string[];
    unread_notifications: number;
    unread_web_notifications: number;
    unread_messages: number;
    current_profile: string;
    _id: string;
    deviceId: string;
    __v: number;
    os: string;
    push_notification_id: string;
    user: IUser;
    user_biometric_rsa: string;
    default_payment_method: string;
    show_introduction?: boolean;
    show_app_guide?: boolean;
    show_project_guide?: boolean;
    /** Vertical video player joyride — separate from show_project_guide. */
    show_video_guide?: boolean;
    show_brand_guide?: boolean;
    show_campaign_creation_guide?: boolean;
    show_campaign_detail_guide?: boolean;
    show_participant_workspace_guide?: boolean;
    already_created_project?: boolean;
    personalized_controls?: Features;
    preferred_verification?: string;
}

export interface UserLocation {
    type: string;
    coordinates: any[];
}

export interface Notifications {
    messages: boolean;
    coming: boolean;
    casting: boolean;
    features: boolean;
    applications: boolean;
}

export interface WebNotificationsSettings {
    project_recommendation: boolean;
    mentions: boolean;
    campaign_activities: boolean;
    shared_content: boolean;
    project_activities: boolean;
    application_activity: boolean;
}

export interface WebCommunicationPreference {
    sms: boolean;
    email: boolean;
    phone: boolean;
}

export interface Location {
    type: string;
    address: string;
    description: string;
    coordinates: number[];
}

export interface IUpdateUserConfig {
    payload: any;
}

export interface IShowProjectGuide {
    on: boolean;
}

export interface IGlobalConfigurationUpdate {
    audition_filters?: {
        rehearsal_date?: 'active' | 'inactive';
        required_media?: 'active' | 'inactive';
        age_range?: 'active' | 'inactive';
        filming_location?: 'active' | 'inactive';
        compensation?: 'active' | 'inactive';
        audition_type?: 'active' | 'inactive';
        required_skill?: 'active' | 'inactive';
        tags?: 'active' | 'inactive';
        employment_type?: 'active' | 'inactive';
    };
    talent_filter?: {
        location?: 'active' | 'inactive';
        age_range?: 'active' | 'inactive';
        height?: 'active' | 'inactive';
        skills?: 'active' | 'inactive';
        sex?: 'active' | 'inactive';
        hair_color?: 'active' | 'inactive';
        body_type?: 'active' | 'inactive';
        weight?: 'active' | 'inactive';
        language_spoken?: 'active' | 'inactive';
        special_skills?: 'active' | 'inactive';
    };
    features?: {
        voting?: 'active' | 'inactive';
        movie_ticket?: 'active' | 'inactive';
        contest_emails?: 'active' | 'inactive';
        audition?: 'active' | 'inactive';
        campaigns?: 'active' | 'inactive';
        buzzbridge?: 'active' | 'inactive';
        discover?: 'active' | 'inactive';
        events?: 'active' | 'inactive';
        talents?: 'active' | 'inactive';
        feed?: 'active' | 'inactive';
        ads?: 'active' | 'inactive';
        hls_play?: 'active' | 'inactive';
        /** When active, search/discovery may use linked IG/TikTok avatars when profile_picture is empty. */
        influencer_platform_avatar_search?: 'active' | 'inactive';
        account_features?: {
            movies?: 'active' | 'inactive';
            productions?: 'active' | 'inactive';
            contributions?: 'active' | 'inactive';
            profile?: 'active' | 'inactive';
            reviews?: 'active' | 'inactive';
            applications?: 'active' | 'inactive';
            settings?: 'active' | 'inactive';
            people?: 'active' | 'inactive';
            tickets?: 'active' | 'inactive';
            likes?: 'active' | 'inactive';
            boxofix_score?: 'active' | 'inactive';
            subscriptions?: 'active' | 'inactive';
        };
        acts?: 'active' | 'inactive';
        social_manager?: 'active' | 'inactive';
    };
    default_casting_limit?: number;
    default_application_limit?: number;
    act_limit?: number;
    contest_submission_limit?: number;
    use_cache?: boolean;
    cache_duration?: number;
    media_control?: {
        file_size_limit?: number;
        mobile_size_limit?: number;
        image_size_limit?: number;
        pagination_count?: number;
    };
    buzzbridge?: {
        enableAutoLink?: {
            Instagram?: boolean;
            YouTube?: boolean;
            TikTok?: boolean;
        };
        instagram_comment_automation_enabled?: boolean;
        /** Instagram Login (instagram_business_basic) — off until Meta approves */
        instagram_login_enabled?: boolean;
        campaign_platforms?: ICampaignPlatformConfig[];
        ugc_budget?: IUgcBudgetConfig;
        creator_payout?: ICreatorPayoutFeeConfig;
        campaign_deadlines?: ICampaignDeadlinesConfig;
        /** Flat platform fee (USD cents) when adding creator seats. Default 2000 ($20). */
        seat_increase_fee_cents?: number;
        deliverable_types?: IDeliverableTypeConfig[];
        duration_ranges?: IDurationRangeConfig[];
    };
    min_ticket_price?: number;
    ticket_flat_fee?: number;
    ticket_additional_fee?: number;
    max_ticket_price?: number;
    ad_control?: IAdControl;
    past_winner_playlist?: string;
    facebook_apps_settings_url?: string;
    /** Minimum payout / withdrawal amount in USD cents. */
    minimum_withdrawal_cents?: number;
    influencer_tiers?: IInfluencerTierConfig[];
    two_fa_configuration?: {
        audme?: boolean;
        email?: boolean;
        sms?: boolean;
        whatsapp?: boolean;
    };
    /** HTML content scrub extras + read-only defaults from GET */
    content_scrub?: ContentScrubEditorState;
    buzzbridge_loyalty?: IBuzzbridgeLoyaltyConfig;
}
